import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, UserPlus, LogIn, Gift, ArrowLeft } from 'lucide-react';
import './ChooseMode.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ChooseMode = () => {
  const navigate = useNavigate();
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);

  const handleGuestMode = () => {
    // Mark as guest mode - don't clear user_session_token (user might want to save debate later)
    // But set a flag that this debate is started as guest
    localStorage.setItem('debate_started_as_guest', 'true');
    navigate('/setup');
  };

  const handleLogin = () => {
    // Clear guest flag if user chooses to login
    localStorage.removeItem('debate_started_as_guest');
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  const handleVoucherAccess = () => {
    setShowVoucherModal(true);
  };

  const handleRedeemExpressVoucher = async () => {
    if (!voucherCode.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }

    setVoucherLoading(true);
    try {
      const response = await axios.post(
        `${API}/voucher/redeem-express?code=${voucherCode.trim()}`
      );

      // Store user session
      localStorage.setItem('user_session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      toast.success('🎉 ' + response.data.message);
      toast.success(`${response.data.debates_limit} debates unlocked until ${response.data.valid_until}`);

      // Redirect to setup
      setTimeout(() => {
        navigate('/setup');
      }, 1500);
    } catch (error) {
      console.error('Error redeeming express voucher:', error);
      toast.error(error.response?.data?.detail || 'Error redeeming voucher');
      setVoucherLoading(false);
    }
  };

  return (
    <div className="choose-mode-container">
       {/* Home Button */}
      <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10 }}>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Home
        </Button>
      </div>
      <div className="choose-mode-content">
        <h1 className="choose-mode-title">How would you like to start?</h1>
        <p className="choose-mode-subtitle">Choose your path to debate</p>

        <div className="mode-cards">
          {/* Guest Mode */}
          <Card className="mode-card" onClick={handleGuestMode}>
            <CardHeader>
              <Users className="mode-icon" size={48} />
              <CardTitle>Start as Guest</CardTitle>
              <CardDescription>Quick start without account</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="mode-features">
                <li>✓ Start immediately</li>
                <li>✓ No registration</li>
                <li>✓ All debate features</li>
              </ul>
              <Button className="mode-button">Start Now</Button>
            </CardContent>
          </Card>

          {/* Login */}
          <Card className="mode-card" onClick={handleLogin}>
            <CardHeader>
              <LogIn className="mode-icon" size={48} />
              <CardTitle>Login</CardTitle>
              <CardDescription>Already registered?</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="mode-features">
                <li>✓ Access your archive</li>
                <li>✓ Resume debates</li>
                <li>✓ Ranking & statistics</li>
              </ul>
              <Button className="mode-button mode-button-primary">Login</Button>
            </CardContent>
          </Card>

          {/* Register */}
          <Card className="mode-card mode-card-featured" onClick={handleRegister}>
            <div className="featured-badge">Recommended</div>
            <CardHeader>
              <UserPlus className="mode-icon mode-icon-featured" size={48} />
              <CardTitle>Register</CardTitle>
              <CardDescription>Create a free account</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="mode-features mode-features-featured">
                <li>✓ Appear in ranking</li>
                <li>✓ Pause debates</li>
                <li>✓ Full archive</li>
                <li>✓ Statistics & progress</li>
              </ul>
              <Button className="mode-button mode-button-featured">Register for free</Button>
            </CardContent>
          </Card>

          {/* VIP Access with Voucher */}
          <Card className="mode-card mode-card-vip" onClick={handleVoucherAccess}>
            <div className="vip-badge">VIP</div>
            <CardHeader>
              <Gift className="mode-icon mode-icon-vip" size={48} />
              <CardTitle>VIP Access 🎟️</CardTitle>
              <CardDescription>Have a voucher code?</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="mode-features mode-features-vip">
                <li>✓ Start immediately</li>
                <li>✓ No registration needed</li>
                <li>✓ All premium features</li>
                <li>✓ For influencers & beta testers</li>
              </ul>
              <Button className="mode-button mode-button-vip">Redeem code</Button>
            </CardContent>
          </Card>
        </div>

        {/* Voucher Modal */}
        {showVoucherModal && (
          <div className="voucher-modal-overlay" onClick={() => !voucherLoading && setShowVoucherModal(false)}>
            <div className="voucher-modal" onClick={(e) => e.stopPropagation()}>
              <h2>🎟️ Unlock VIP Access</h2>
              <p className="voucher-modal-description">
                Enter your voucher code and start immediately - no registration required!
              </p>
              
              <Input
                type="text"
                placeholder="XXXX-XXXX-XXXX"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                className="voucher-modal-input"
                disabled={voucherLoading}
                autoFocus
              />

              <div className="voucher-modal-actions">
                <Button
                  variant="outline"
                  onClick={() => setShowVoucherModal(false)}
                  disabled={voucherLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRedeemExpressVoucher}
                  disabled={voucherLoading || !voucherCode.trim()}
                  className="voucher-modal-submit"
                >
                  {voucherLoading ? 'Checking code...' : 'Redeem code & start'}
                </Button>
              </div>

              <p className="voucher-modal-hint">
                ℹ️ You'll get automatic access without further steps
              </p>
            </div>
          </div>
        )}

        <div className="choose-mode-footer">
          <p>By using this service you accept our 
            <a href="/privacy-policy"> Privacy Policy</a> and 
            <a href="/terms"> Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChooseMode;
